/**
 * NKBA API
 * The RESTful backend behind the NKBA application
 *
 * OpenAPI spec version: V1.0.0
 * Contact: cloud@nkba.org
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */

import { AxiosRequestConfig } from 'axios';
import { axios } from '../axios.config';
import { ChangePasswordRequest } from '../models/ChangePasswordRequest';
import { EmailRequest } from '../models/EmailRequest';
import { Member } from '../models/Member';
import { MemberPermissions } from '../models/MemberPermissions';
import { ResultsPartialMemberObjectId } from '../models/ResultsPartialMemberObjectId';
import { User } from '../models/User';

/* tslint:disable:no-unused-variable member-ordering max-line-length */

export class MemberApiResource {

    /**
     * Change a member&#39;s password
     * 
     * @param memberId Member id
     * @param body 
     */
    public changePassword(memberId: string, body?: ChangePasswordRequest, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v2/member/{memberId}/password'
            .replace('{' + 'memberId}', String(memberId));
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Create a new member
     * 
     * @param body Member data to create
     */
    public createMember(body?: Member, axiosConfig?: AxiosRequestConfig): Promise<Member> {
        const reqPath = '/v2/member';
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Export members CSV file
     * 
     * @param q Search query
     */
    public exportCsv(query?: { q?: string }, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v2/member/export';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Find members by a search query
     * 
     * @param orgMemberId Org member id to search strictly within
     * @param q Search query
     * @param from Member id to start from
     * @param limit Number of results to return
     */
    public findMembers(query?: { orgMemberId?: string, q?: string, from?: string, limit?: number }, axiosConfig?: AxiosRequestConfig): Promise<ResultsPartialMemberObjectId> {
        const reqPath = '/v2/member';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return the currently logged in member
     * 
     */
    public getMe(axiosConfig?: AxiosRequestConfig): Promise<Member> {
        const reqPath = '/v2/member/me';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return the currently logged in member, with permissions
     * 
     */
    public getMePermissions(axiosConfig?: AxiosRequestConfig): Promise<MemberPermissions> {
        const reqPath = '/v2/member/me/permissions';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a particular member by id
     * 
     * @param memberId Member id
     * @param expandTags Expand tags property
     * @param expandAwards Expand awards property
     */
    public getMember(memberId: string, query?: { expandTags?: boolean, expandAwards?: boolean }, axiosConfig?: AxiosRequestConfig): Promise<Member> {
        const reqPath = '/v2/member/{memberId}'
            .replace('{' + 'memberId}', String(memberId));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a particular member by number
     * 
     * @param num Member number
     * @param expandTags Expand tags property
     * @param expandAwards Expand awards property
     */
    public getMemberByNumber(num: number, query?: { expandTags?: boolean, expandAwards?: boolean }, axiosConfig?: AxiosRequestConfig): Promise<Member> {
        const reqPath = '/v2/member/number/{num}'
            .replace('{' + 'num}', String(num));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a list of members by chapterId
     * 
     * @param chapterId Chapter Id
     */
    public getMembersByChapterId(
        chapterId: string,
        role: string,
        axiosConfig?: AxiosRequestConfig
    ): Promise<ResultsPartialMemberObjectId> {
        const reqPath = ('/v2/member/chapter/{chapterId}?role=' + role)
            .replace('{' + 'chapterId}', String(chapterId));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath
        };
        return axios.request(reqConfig).then(res => {
            return res.data;
        });
    }

    /**
     * Return a particular member by slug
     * 
     * @param slug Member slug
     * @param expandTags Expand tags property
     * @param expandAwards Expand awards property
     */
    public getMemberBySlug(slug: string, query?: { expandTags?: boolean, expandAwards?: boolean }, axiosConfig?: AxiosRequestConfig): Promise<Member> {
        const reqPath = '/v2/member/slug/{slug}'
            .replace('{' + 'slug}', String(slug));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Invite a user to an organization, creating the user if the email does not exist
     * 
     * @param body Member data to create or invite
     */
    public inviteUser(body?: Member, axiosConfig?: AxiosRequestConfig): Promise<User> {
        const reqPath = '/v2/member/invite';
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Send a password reset email
     * 
     * @param body User email address
     */
    public sendResetEmail(body?: EmailRequest, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v2/member/sendreset';
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Update an existing member
     * 
     * @param memberId Member id
     * @param body Member data to update
     */
    public updateMember(memberId: string, body?: Member, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v2/member/{memberId}'
            .replace('{' + 'memberId}', String(memberId));
        let reqConfig = {
            ...axiosConfig,
            method: 'PATCH',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

}

export const MemberApi = new MemberApiResource();



// WEBPACK FOOTER //
// ./src/common/api/resources/Member2Api.ts